import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'

import BaseButton from '@/components/common/BaseButton'

type PermanentDeleteDialogProps = {
  count: number
  entity: 'time entry' | 'activity'
  loading: boolean
  onClose: () => void
  onConfirm: () => void | Promise<void>
  closeOnBackdrop?: boolean
  workItemCountInEntry?: number
  children?: React.ReactNode
  confirmDisabled?: boolean
  deletesTimeEntry?: boolean
}

/** Shared MIRA-4295 confirmation for time entries and captured activities. */
export function PermanentDeleteDialog({
  count,
  entity,
  loading,
  onClose,
  onConfirm,
  closeOnBackdrop = false,
  workItemCountInEntry,
  children,
  confirmDisabled = false,
  deletesTimeEntry,
}: PermanentDeleteDialogProps) {
  const isWorkItemDeletion = workItemCountInEntry != null
  const onlyWorkItemInEntry = workItemCountInEntry === 1
  const removesTimeEntry = deletesTimeEntry ?? onlyWorkItemInEntry
  const plural = entity === 'activity' ? 'activities' : 'time entries'
  const title =
    isWorkItemDeletion
      ? removesTimeEntry
        ? 'Delete work item and time entry'
        : 'Delete work item'
      : count > 1
        ? `Permanently delete selected ${plural}?`
        : `Permanently delete this ${entity}?`
  const confirmLabel = removesTimeEntry
    ? 'Delete Work Item and Time Entry'
    : 'Delete'

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !loading) onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [loading, onClose])

  return createPortal(
    <div
      className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/30 px-4"
      onClick={event => {
        event.stopPropagation()
        if (closeOnBackdrop && event.target === event.currentTarget && !loading) {
          onClose()
        }
      }}
      onMouseDown={event => event.stopPropagation()}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        className={`box-border w-full ${removesTimeEntry ? 'max-w-[400px]' : 'max-w-[360px]'} overflow-hidden rounded-2xl bg-white shadow-[0px_4px_8px_3px_rgba(0,0,0,0.15),0px_1px_3px_0px_rgba(0,0,0,0.3)]`}
      >
        <p className="m-0 px-6 pb-6 pt-6 text-sm font-normal leading-5 tracking-[0.25px] text-[#525252]">
          {isWorkItemDeletion ? (
            removesTimeEntry ? (
              onlyWorkItemInEntry
                ? 'There is only one work item in this time entry. Deleting this work item will also delete the time entry. This action cannot be undone.'
                : 'Deleting these work items will also delete the time entry. This action cannot be undone.'
            ) : (
              <>
                Permanently delete this work item.
                <br />
                This action cannot be undone.
              </>
            )
          ) : (
            <>
              {title}
              <br />
              {"This action can't be undone."}
            </>
          )}
        </p>
        {children && <div className="px-6 pb-6 text-sm leading-5 text-[#525252]">{children}</div>}
        <div className={`flex justify-end gap-2 px-6 pb-6 ${removesTimeEntry ? 'max-[440px]:flex-col' : ''}`}>
          <button
            type="button"
            disabled={loading}
            onClick={onClose}
            className={`h-8 cursor-pointer rounded-full border border-solid border-[#DADADA] bg-white px-3 text-sm font-normal text-[#333] disabled:cursor-not-allowed disabled:opacity-50 ${removesTimeEntry ? 'max-[440px]:w-full' : ''}`}
          >
            Cancel
          </button>
          <BaseButton
            disabled={confirmDisabled}
            loading={loading}
            loader={{ loaderSize: 'xsmall' }}
            onClick={() => void onConfirm()}
            className={`h-8 !min-h-8 whitespace-nowrap !px-3 !py-0 ${removesTimeEntry ? 'max-[440px]:w-full max-[440px]:whitespace-normal' : ''}`}
          >
            {confirmLabel}
          </BaseButton>
        </div>
      </div>
    </div>,
    document.body
  )
}
